import { useCallback } from 'react'
import useSWRInfinite from 'swr/infinite'
import { z } from 'zod'
import { ESGlossary } from '../../elastic_search'
import { fb } from '../../firebase'
import { GlossariesSearchEndpointParams } from '../../shared/firebase/functions/search_endpoint'
import { deserializeArray } from '../../utils'

type Params = z.input<typeof GlossariesSearchEndpointParams>
type SWRKey = {
  index: 'glossaries'
  params: Params
  page: number
  size: number
}

const PAGE_SIZE = 20

const fetchGlossaries = async (key: SWRKey): Promise<ESGlossary[]> => {
  const { data } = await fb.call('search')(key)
  if (data.index !== 'glossaries') throw new Error('Unexpected result.')
  return deserializeArray(data.result, {})
}

export const useESGlossaries = (
  params: Params | null,
  fallback?: ESGlossary[]
) => {
  const getKey = useCallback(
    (index: number): SWRKey | null => {
      if (params === null) return null
      return { params, page: index + 1, size: PAGE_SIZE, index: 'glossaries' }
    },
    [params]
  )
  const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<
    ESGlossary[]
  >(getKey, fetchGlossaries, {
    fallbackData: fallback ? [fallback] : undefined,
  })

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = data?.[0] !== undefined ? data[0].length === 0 : undefined
  const isReachingEnd =
    data !== undefined
      ? data.length >= 2 &&
        data[data.length - 2]?.length < PAGE_SIZE &&
        data[data.length - 1]?.length === 0
      : undefined
  const isRefreshing = isValidating && data && data.length === size

  const glossaries: ESGlossary[] | undefined =
    data && data.length > 0 ? ([] as ESGlossary[]).concat(...data) : undefined

  const loadMore = async () => {
    await setSize(size + 1)
  }

  return {
    glossaries,
    loadMore,
    isReachingEnd,
    isLoadingMore,
    isEmpty,
    isRefreshing,
  }
}
